import * as React from 'react';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { AnalysisResult } from './useAnalyseCSV';
import { ColumnMapping, MULTI_COLUMN_FIELDS } from './autoMap';
import { ImportOptions } from './ImportOptions';
import { saveMapping } from './useMappingStorage';

export interface MappingField {
    id: string;
    label: string;
}

interface Props {
    result: AnalysisResult;
    mapping: ColumnMapping;
    onMappingChange: (m: ColumnMapping) => void;
    fields: MappingField[];
    options: ImportOptions;
    onOptionsChange: (options: ImportOptions) => void;
}

interface DragInfo {
    colIndex: number;
    fromField: string | null; // null = dragged from unmapped panel
}

function applyAssign(
    mapping: ColumnMapping,
    colIndex: number,
    toField: string,
    fromField: string | null,
): ColumnMapping {
    const fieldMap: Record<string, number[]> = {};
    for (const [k, v] of Object.entries(mapping.fieldMap)) fieldMap[k] = [...v];
    let unmappedCols = [...mapping.unmappedCols];

    // Remove from source
    if (fromField !== null) {
        fieldMap[fromField] = (fieldMap[fromField] ?? []).filter(i => i !== colIndex);
        if (fieldMap[fromField].length === 0) delete fieldMap[fromField];
    } else {
        unmappedCols = unmappedCols.filter(i => i !== colIndex);
    }

    fieldMap[toField] = [...(fieldMap[toField] ?? []), colIndex];
    return { fieldMap, unmappedCols };
}

function applyRemove(mapping: ColumnMapping, colIndex: number, fromField: string): ColumnMapping {
    const fieldMap: Record<string, number[]> = {};
    for (const [k, v] of Object.entries(mapping.fieldMap)) fieldMap[k] = [...v];
    fieldMap[fromField] = (fieldMap[fromField] ?? []).filter(i => i !== colIndex);
    if (fieldMap[fromField].length === 0) delete fieldMap[fromField];
    return { fieldMap, unmappedCols: [...mapping.unmappedCols, colIndex] };
}

const MappingStep: React.FC<Props> = ({
    result,
    mapping,
    onMappingChange,
    fields,
    options,
    onOptionsChange,
}) => {
    const t = useTranslations();
    const [dragOverTarget, setDragOverTarget] = React.useState<string | 'unmapped' | null>(null);

    React.useEffect(() => {
        saveMapping(mapping.fieldMap, result.headers);
    }, [mapping, result.headers]);

    const handleDragStart = (e: React.DragEvent, colIndex: number, fromField: string | null) => {
        e.dataTransfer.setData('text/plain', JSON.stringify({ colIndex, fromField }));
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDropOnField = (e: React.DragEvent, toField: string) => {
        e.preventDefault();
        setDragOverTarget(null);
        try {
            const { colIndex, fromField }: DragInfo = JSON.parse(e.dataTransfer.getData('text/plain'));
            onMappingChange(applyAssign(mapping, colIndex, toField, fromField));
        } catch { /* ignore */ }
    };

    const handleDropOnUnmapped = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOverTarget(null);
        try {
            const { colIndex, fromField }: DragInfo = JSON.parse(e.dataTransfer.getData('text/plain'));
            if (fromField !== null) onMappingChange(applyRemove(mapping, colIndex, fromField));
        } catch { /* ignore */ }
    };

    const handleDragEnter = (e: React.DragEvent, target: string | 'unmapped') => {
        e.preventDefault();
        setDragOverTarget(target);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setDragOverTarget(null);
        }
    };

    const sampleRow = result.sampleRows[0] ?? [];

    const renderChip = (colIndex: number, fromField: string | null) => {
        const header = result.headers[colIndex] ?? `col${colIndex}`;
        const sample = sampleRow[colIndex];
        return (
            <span
                key={colIndex}
                draggable
                onDragStart={e => handleDragStart(e, colIndex, fromField)}
                title={sample ? `e.g. ${sample}` : header}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--spacer-XXS)',
                    background: 'white',
                    border: 'var(--border-thin) solid var(--appLigthGrey)',
                    borderRadius: '12px',
                    padding: '2px var(--spacer-XS)',
                    fontSize: '0.8rem',
                    cursor: 'grab',
                    userSelect: 'none' as const,
                    whiteSpace: 'nowrap' as const,
                    maxWidth: '180px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{header}</span>
                {fromField !== null && (
                    <button
                        onClick={() => onMappingChange(applyRemove(mapping, colIndex, fromField))}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0',
                            lineHeight: 1,
                            color: 'var(--text-muted)',
                            fontSize: '0.9rem',
                            flexShrink: 0,
                        }}
                    >
                        ×
                    </button>
                )}
            </span>
        );
    };

    return (
        <div style={{ display: 'flex', gap: 'var(--spacer-M)', height: '65vh' }}>
            {/* Left: field rows */}
            <div style={{ flex: 3, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {fields.map(field => {
                    const assignedCols = mapping.fieldMap[field.id] ?? [];
                    const isOver = dragOverTarget === field.id;
                    return (
                        <div
                            key={field.id}
                            onDragOver={e => { e.preventDefault(); setDragOverTarget(field.id); }}
                            onDragEnter={e => handleDragEnter(e, field.id)}
                            onDragLeave={handleDragLeave}
                            onDrop={e => handleDropOnField(e, field.id)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacer-S)',
                                padding: 'var(--spacer-XS) var(--spacer-S)',
                                borderRadius: '6px',
                                border: `var(--border-thin) solid ${isOver ? 'var(--appBlue)' : 'var(--appLigthGrey)'}`,
                                background: isOver ? 'var(--bg-hover-light)' : 'white',
                                minHeight: '38px',
                                transition: 'border-color 0.1s, background 0.1s',
                            }}
                        >
                            <span style={{
                                width: '160px',
                                flexShrink: 0,
                                fontSize: '0.8rem',
                                color: 'var(--text-muted)',
                                fontWeight: 600,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>
                                {field.label}
                            </span>
                            <div style={{
                                flex: 1,
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 'var(--spacer-XXS)',
                                alignItems: 'center',
                                minWidth: 0,
                            }}>
                                {assignedCols.length > 0
                                    ? assignedCols.map(i => renderChip(i, field.id))
                                    : (
                                        <span style={{ fontSize: '0.75rem', color: 'var(--text-placeholder)', fontStyle: 'italic' }}>
                                            {t(TK.importClientsDragHere)}
                                        </span>
                                    )
                                }
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Right: unmapped columns */}
            <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
                <div style={{
                    border: 'var(--border-thin) solid var(--appLigthGrey)',
                    borderRadius: '6px',
                    background: 'white',
                    padding: 'var(--spacer-S)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--spacer-XS)',
                }}>
                    <div style={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: 'var(--text-muted)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                    }}>
                        {t(TK.options)}
                    </div>
                    <label style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 'var(--spacer-XS)',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        color: 'var(--text-body)',
                        lineHeight: 1.4,
                    }}>
                        <input
                            type="checkbox"
                            checked={options.skipExistingOnly}
                            onChange={e => onOptionsChange({ ...options, skipExistingOnly: e.target.checked })}
                            style={{
                                marginTop: '2px',
                                flexShrink: 0,
                                accentColor: 'var(--appBlue)',
                            }}
                        />
                        <span>{t(TK.importClientsSkipExistingOnly)}</span>
                    </label>
                </div>
                <div style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                }}>
                    {t(TK.importClientsUnmappedColumns)}
                    {mapping.unmappedCols.length > 0 && (
                        <span style={{ marginLeft: 'var(--spacer-XS)', fontWeight: 400 }}>
                            ({mapping.unmappedCols.length})
                        </span>
                    )}
                </div>
                <div
                    onDragOver={e => { e.preventDefault(); setDragOverTarget('unmapped'); }}
                    onDragEnter={e => handleDragEnter(e, 'unmapped')}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDropOnUnmapped}
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        border: `var(--border-thin) dashed ${dragOverTarget === 'unmapped' ? 'var(--appBlue)' : 'var(--appLigthGrey)'}`,
                        background: dragOverTarget === 'unmapped' ? 'var(--bg-hover-light)' : 'var(--bg-light)',
                        borderRadius: '6px',
                        padding: 'var(--spacer-S)',
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignContent: 'flex-start',
                        gap: 'var(--spacer-XS)',
                        transition: 'border-color 0.1s, background 0.1s',
                    }}
                >
                    {mapping.unmappedCols.map(i => renderChip(i, null))}
                    {mapping.unmappedCols.length === 0 && (
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-placeholder)', fontStyle: 'italic' }}>
                            —
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MappingStep;
